import { useEffect, useRef, useState } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useNavigate, useParams } from 'react-router-dom';

import { type AvatarUploadRef, showToastMessage } from 'enable-ui';
import { useFormik } from 'formik';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';

import type { AdminUser } from '../type';

import {
  getExtFileName,
  getInputStatus,
  hasEmptyValues,
  isValidEmail,
  randomString,
  trimObject,
} from 'helpers';
import { UploadFile } from 'helpers/upload-file';
import { useBoolean, useString } from 'hooks';
import { apiCall, useFetchApiToState } from 'hooks/service';
import { isEmpty } from 'lodash';

import { ERROR_MESSAGE, SUCCESS_MESSAGE } from 'constants/message';
import { PATH } from 'constants/path';

import { defaultAdminForm } from '../constant';
import { formAdminSchema, formUpdateAdminSchema } from '../schema';

export const useAdminFormController = () => {
  const userProfile = useAuthStore(userProfileSelector);

  const navigation = useNavigate();

  const params = useParams();

  const { id } = params;

  const isAddUser = !id;

  const roleKey = userProfile?.role?.key || '';

  const logoRef = useRef<AvatarUploadRef>(null);

  const logoURL = useString('');

  const [logo, setLogo] = useState<File | null>(null);

  const isErrorAvatar = useBoolean(false);

  const isRemoveLogo = useBoolean(false);

  const adminDetailState = useFetchApiToState<any, any>({
    url: `/users/${id}`,
    method: 'GET',
    isLoading: true,
  });

  const adminDetail: AdminUser = adminDetailState.state;

  useEffect(() => {
    if (!id) {
      return;
    }

    adminDetailState.fetchApi();
  }, [id]);

  useEffect(() => {
    if (isEmpty(adminDetail)) {
      return;
    }

    setValues({
      email: adminDetail.email,
      gender: adminDetail?.physicalProfile?.gender || '',
      password: adminDetail.password || '',
      phone: adminDetail?.phone || '',
      firstName: adminDetail?.firstName || '',
      lastName: adminDetail.lastName,
      avatar: adminDetail?.physicalProfile?.avatar || '',
    });

    logoURL.setValue(adminDetail?.physicalProfile?.avatar || '');
  }, [JSON.stringify(adminDetail)]);

  const formik = useFormik({
    initialValues: defaultAdminForm,
    validationSchema: id ? formUpdateAdminSchema : formAdminSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      let avatar = '';

      if (logo) {
        const ext = getExtFileName(logo.name);

        avatar = `admin-${randomString(4)}-${Date.now()}${ext}`;

        await UploadFile(logo, avatar);
      }

      const request: any = {
        ...trimObject(values),
        name: `${values.firstName || ''} ${values.lastName || ''}`.trim(),
        avatar,
      };

      if (isAddUser || !values.password) {
        delete request.password;
      }

      const result: AdminUser = await apiCall({
        url: id ? `/users/${id}/admin` : '/users/admin',
        data: request,

        method: id ? 'PATCH' : 'POST',
        isLoading: true,
        showError: true,
      });

      if (!result.id) {
        return;
      }

      if (id) {
        adminDetailState.fetchApi();
        showToastMessage('success', SUCCESS_MESSAGE.UPDATE_ADMIN);

        return;
      }

      navigation(PATH.ADMINS);
      showToastMessage('success', 'Added admin user successfully!');
    },
  });

  const {
    values,
    isSubmitting,
    isValid,
    handleSubmit,
    handleChange,
    setFieldValue,
    setValues,
    errors,
  } = formik;

  const onChangeGender = (newGenderKey: string) => () => {
    setFieldValue('gender', newGenderKey);
  };

  const handleChangePhone = (value: string) => {
    setFieldValue('phone', value || '');
  };

  const emailStatus = getInputStatus(
    values.email,
    SUCCESS_MESSAGE.EMAIL,
    isValidEmail(values.email.trim()).isValid ? '' : isValidEmail(values.email.trim()).message,
  );

  const disabled =
    hasEmptyValues({ firstName: values.firstName }) ||
    isSubmitting ||
    !isValid ||
    (isValidEmail(values.email.trim()).isValid ? false : true) ||
    (values.phone ? !isValidPhoneNumber(values.phone) : false);

  const disabledButton =
    isSubmitting ||
    !isValid ||
    (values.phone ? !isValidPhoneNumber(values.phone) : false) ||
    (values.phone?.trim() === String(adminDetail?.phone ?? '') &&
      values.gender === String(adminDetail?.physicalProfile?.gender || '') &&
      values.password === '' &&
      values.firstName === String(adminDetail?.firstName ?? '') &&
      values.lastName === adminDetail?.lastName &&
      values.email === adminDetail?.email &&
      (logo ? logoURL.value === adminDetail?.physicalProfile?.avatar : true));

  const goBack = () => {
    navigation(PATH.ADMINS);
  };

  const onChangeAvatar = (file: File) => {
    setLogo(file);
    logoURL.setValue(URL.createObjectURL(file));
  };

  const onErrorAvatar = (file: File) => {
    isErrorAvatar.setValue(Boolean(file));
  };

  const onDeleteLogo = () => {
    logoRef.current?.clearAvatar();
    setLogo(null);

    if (logoURL.value) {
      isRemoveLogo.setValue(true);
    }
  };

  const onCancel = () => {
    if (id) {
      setValues({
        email: adminDetail.email,
        gender: adminDetail?.physicalProfile?.gender || '',
        password: adminDetail.password || '',
        phone: adminDetail?.phone || '',
        firstName: adminDetail?.firstName || '',
        lastName: adminDetail.lastName,
        avatar: adminDetail?.physicalProfile?.avatar || '',
      });

      logoURL.setValue(adminDetail.physicalProfile?.avatar || '');

      setLogo(null);

      return;
    }

    formik.setValues(defaultAdminForm);
    navigation(`${PATH.ADMINS}`);
  };

  return {
    ...values,
    roleKey,
    disabled,
    emailStatus,
    handleChange,
    onChangeGender,
    handleSubmit,
    handleChangePhone,
    goBack,
    logoRef,
    onChangeAvatar,
    logoURL,
    onErrorAvatar,
    isErrorAvatar: isErrorAvatar.value,
    onDeleteLogo,
    logo,
    adminUserId: id,
    disabledButton,
    onCancel,
    isAddUser,
    adminDetail,
  };
};
